import { STAR_CLASSES, blackbodyRgb, type FactionId } from '@sd/content';
import { Rng, hexKey, hexToPixel, sectorOf, type Hex, type Planet, type StarSystem } from '@sd/sim';
import {
  AdditiveBlending,
  OctahedronGeometry,
  BufferGeometry,
  CanvasTexture,
  CircleGeometry,
  Color,
  Float32BufferAttribute,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  MeshStandardMaterial,
  Object3D,
  Points,
  RingGeometry,
  SRGBColorSpace,
  Sprite,
  SpriteMaterial,
  TorusGeometry,
  Vector3,
  type MeshStandardMaterialParameters,
} from 'three';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import { buildSky } from './map/sky';
import { LAYER_NO_OUTLINE } from './outline';
import { PLANET_COLORS, SCENE_COLORS, seatColorHex } from './palette';

/** World units per hex circumradius. The map lies in the XZ plane. */
export const HEX_SIZE = 1;

export function hexToWorld(h: Hex, y = 0): Vector3 {
  const { x, y: z } = hexToPixel(h, HEX_SIZE);
  return new Vector3(x, y, z);
}

export interface HomeLabel {
  playerId: string;
  faction: FactionId;
  element: HTMLDivElement;
}

export interface SystemView {
  root: Group;
  /** Called every frame with elapsed seconds. */
  update(seconds: number): void;
  labels: HomeLabel[];
  /**
   * Tints the tiles a crisis will strike (#55): one sector, every tile
   * (null, the final crisis), or none (undefined).
   */
  tintSector(sector: number | null | undefined): void;
  /**
   * The fog of war (#33, #97): tiles not in `visible` are dimmed; those not
   * in `explored` go dark and hide what lies on them. Null lifts the fog.
   */
  setFog(visible: ReadonlySet<string> | null, explored?: ReadonlySet<string> | null): void;
  /** Clouds scanned to the end fade away (#103): ids of every such cloud so far. */
  disperseClouds(cloudIds: ReadonlySet<string>): void;
  dispose(): void;
}

function flatMaterial(color: string, extra: MeshStandardMaterialParameters = {}) {
  return new MeshStandardMaterial({
    color,
    flatShading: true,
    roughness: 0.85,
    metalness: 0.05,
    ...extra,
  });
}

/** Share of the star's heat mixed into a struck tile's colour. */
const STRIKE_TINT = 0.3;
/** Brightness of a tile the player does not see right now (#33)... */
const FOG_DIM = 0.45;
/** ...and of one it has never seen (#97): all but black. */
const UNEXPLORED_DIM = 0.08;
/** The grid's fill and line opacity (#99): space shows through. */
const TILE_FILL = 0.09;
const TILE_LINE = 0.4;
/** A never-seen tile is veiled in near-black (#97, #99): the classic fog. */
const SHROUD = 0.88;
/** Lagrange points: the exotics' violet (tokens.css --good-exotics). */
const LAGRANGE_COLOR = '#d78cff';

/**
 * The hex grid (#99, the author's look of 21.10.2026): thin outlines and a
 * faint fill, about a tenth opaque, so space shows through and everything
 * on the map stands above the grid. Neither writes depth, so the outline
 * pass draws no dark seams between tiles.
 */
function buildTiles(system: StarSystem): {
  group: Group;
  tint(sector: number | null | undefined): void;
  fog(visible: ReadonlySet<string> | null, explored: ReadonlySet<string> | null): void;
} {
  const orbitRings = new Set(system.orbits.map((o) => o.ringRadius));
  const corona = new Set(system.coronaHexes.map(hexKey));
  const star = new Set(system.starHexes.map(hexKey));
  const hexes: Hex[] = [];
  for (let q = -system.mapRadius; q <= system.mapRadius; q++) {
    for (let r = -system.mapRadius; r <= system.mapRadius; r++) {
      const s = -q - r;
      if (Math.max(Math.abs(q), Math.abs(r), Math.abs(s)) <= system.mapRadius) hexes.push({ q, r });
    }
  }
  const tiles = hexes.filter((h) => !star.has(hexKey(h)));
  const group = new Group();

  // The fill: one flat hexagon per tile, tinted per tile.
  const fillGeometry = new CircleGeometry(HEX_SIZE * 0.96, 6)
    .rotateZ(Math.PI / 6)
    .rotateX(-Math.PI / 2);
  const fill = new InstancedMesh(
    fillGeometry,
    new MeshBasicMaterial({ transparent: true, opacity: TILE_FILL, depthWrite: false }),
    tiles.length,
  );
  const m = new Matrix4();
  const color = new Color();
  const base: Color[] = [];
  tiles.forEach((h, i) => {
    const p = hexToWorld(h, 0);
    m.makeTranslation(p.x, p.y, p.z);
    fill.setMatrixAt(i, m);
    const ring = (Math.abs(h.q) + Math.abs(h.r) + Math.abs(-h.q - h.r)) / 2;
    const tint = corona.has(hexKey(h))
      ? SCENE_COLORS.tileCorona
      : orbitRings.has(ring)
        ? SCENE_COLORS.tileOrbit
        : SCENE_COLORS.tile;
    base.push(new Color(tint));
    fill.setColorAt(i, color.set(tint));
  });
  fill.instanceMatrix.needsUpdate = true;
  fill.renderOrder = -2;
  fill.layers.set(LAYER_NO_OUTLINE);
  group.add(fill);

  // The shroud over never-seen tiles: a dark veil a little above the grid; a seen tile's
  // veil shrinks to nothing.
  const shroud = new InstancedMesh(
    new CircleGeometry(HEX_SIZE, 6).rotateZ(Math.PI / 6).rotateX(-Math.PI / 2),
    new MeshBasicMaterial({
      color: SCENE_COLORS.shroud,
      transparent: true,
      opacity: SHROUD,
      depthWrite: false,
    }),
    tiles.length,
  );
  const hidden = new Matrix4().makeScale(0, 0, 0);
  const veils = tiles.map((h) => {
    const p = hexToWorld(h, 0.02);
    return new Matrix4().makeTranslation(p.x, p.y, p.z);
  });
  tiles.forEach((_, i) => shroud.setMatrixAt(i, hidden));
  // Its bounds are taken once, while every veil is hidden at the centre: culled by
  // them, the shroud vanished at some camera angles (#150). It covers the map anyway.
  shroud.frustumCulled = false;
  shroud.renderOrder = 1;
  shroud.layers.set(LAYER_NO_OUTLINE);
  group.add(shroud);

  // The outlines: six edges per tile in one draw call, coloured per tile for the fog.
  const corners = Array.from({ length: 6 }, (_, k) => {
    const a = ((60 * k - 30) * Math.PI) / 180;
    return [Math.cos(a) * HEX_SIZE * 0.98, Math.sin(a) * HEX_SIZE * 0.98] as const;
  });
  const positions: number[] = [];
  tiles.forEach((h) => {
    const p = hexToWorld(h, 0.01);
    for (let k = 0; k < 6; k++) {
      const [ax, az] = corners[k]!;
      const [bx, bz] = corners[(k + 1) % 6]!;
      positions.push(p.x + ax, p.y, p.z + az, p.x + bx, p.y, p.z + bz);
    }
  });
  const lineGeometry = new BufferGeometry();
  lineGeometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  const lineColors = new Float32BufferAttribute(new Float32Array(positions.length), 3);
  lineGeometry.setAttribute('color', lineColors);
  const lines = new LineSegments(
    lineGeometry,
    new LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: TILE_LINE,
      depthWrite: false,
    }),
  );
  lines.renderOrder = -1;
  lines.layers.set(LAYER_NO_OUTLINE);
  group.add(lines);

  const sectors = tiles.map(sectorOf);
  const keys = tiles.map(hexKey);
  const heat = new Color(SCENE_COLORS.strike);
  const edge = new Color(SCENE_COLORS.tileEdge);
  let struckSector: number | null | undefined;
  let visible: ReadonlySet<string> | null = null;
  let explored: ReadonlySet<string> | null = null;
  const paint = () => {
    tiles.forEach((_, i) => {
      const struck =
        struckSector !== undefined && (struckSector === null || sectors[i] === struckSector);
      // Fog of war (#33, #97): unseen tiles are dimmed, never-seen ones all but gone.
      const dim =
        explored && !explored.has(keys[i]!)
          ? UNEXPLORED_DIM
          : visible && !visible.has(keys[i]!)
            ? FOG_DIM
            : 1;
      color.copy(base[i]!);
      if (struck) color.lerp(heat, STRIKE_TINT * 2);
      fill.setColorAt(i, color.multiplyScalar(dim));
      color.copy(edge);
      if (struck) color.lerp(heat, STRIKE_TINT);
      color.multiplyScalar(dim);
      for (let v = 0; v < 12; v++) lineColors.setXYZ(i * 12 + v, color.r, color.g, color.b);
    });
    if (fill.instanceColor) fill.instanceColor.needsUpdate = true;
    lineColors.needsUpdate = true;
    tiles.forEach((_, i) =>
      shroud.setMatrixAt(i, explored && !explored.has(keys[i]!) ? veils[i]! : hidden),
    );
    shroud.instanceMatrix.needsUpdate = true;
  };
  paint();
  return {
    group,
    tint(sector) {
      struckSector = sector;
      paint();
    },
    fog(seen, known) {
      visible = seen;
      explored = known;
      paint();
    },
  };
}

/**
 * A body's radius on the map (#103: a little larger than before, so planets
 * and asteroids read at a glance). Asteroids: the cluster's centre height.
 */
export function bodyRadius(planet: Pick<Planet, 'kind' | 'homeOf'>): number {
  if (planet.kind === 'gasGiant') return 0.86;
  if (planet.kind === 'asteroids') return 0.26;
  return planet.homeOf ? 0.7 : 0.6;
}

/** Height of a body's top above the grid: where an extraction site stands on it. */
export function bodyTop(planet: Pick<Planet, 'kind' | 'homeOf'>): number {
  const r = bodyRadius(planet);
  return planet.kind === 'asteroids' ? r + 0.3 : 2 * r + 0.08;
}

/** Soft radial gradient for star glow, drawn once on a small canvas. */
function glowTexture(): CanvasTexture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,0.9)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.35)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

function buildStar(system: StarSystem): { group: Group; light: Color } {
  const def = STAR_CLASSES[system.starClass];
  const [r, g, b] = blackbodyRgb(def.temperatureK);
  const light = new Color(r, g, b);
  const group = new Group();
  const radius = HEX_SIZE * (0.75 + (def.radiusHexes - 1) * 1.6);
  const core = new Mesh(
    new IcosahedronGeometry(radius, 1),
    new MeshBasicMaterial({ color: light }),
  );
  core.position.y = radius * 0.6;
  group.add(core);
  const glow = new Sprite(
    new SpriteMaterial({
      map: glowTexture(),
      color: light,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    }),
  );
  glow.scale.setScalar(radius * 7);
  glow.position.copy(core.position);
  glow.layers.set(LAYER_NO_OUTLINE);
  group.add(glow);
  return { group, light };
}

function buildPlanets(system: StarSystem, playerFactions: ReadonlyMap<string, FactionId>) {
  const group = new Group();
  const spinning: Object3D[] = [];
  const labels: HomeLabel[] = [];
  /** What stands on each hex, hidden while the hex is unexplored (#97). */
  const byHex = new Map<string, Object3D[]>();
  const put = (h: Hex, object: Object3D) => {
    const key = hexKey(h);
    byHex.set(key, [...(byHex.get(key) ?? []), object]);
    group.add(object);
  };
  for (const planet of system.planets) {
    const big = planet.kind === 'gasGiant';
    const radius = bodyRadius(planet);
    const position = hexToWorld(planet.hex, radius + 0.08);
    const material = flatMaterial(PLANET_COLORS[planet.kind]);

    if (planet.kind === 'asteroids') {
      const rng = Rng.fromSeed(`asteroids:${planet.id}`);
      const cluster = new Group();
      for (let i = 0; i < 5; i++) {
        const rock = new Mesh(new IcosahedronGeometry(rng.range(0.15, 0.29), 0), material);
        rock.position.set(rng.range(-0.5, 0.5), rng.range(0, 0.28), rng.range(-0.5, 0.5));
        rock.rotation.set(rng.range(0, 3), rng.range(0, 3), 0);
        cluster.add(rock);
      }
      cluster.position.copy(position);
      put(planet.hex, cluster);
      spinning.push(cluster);
      continue;
    }

    const body = new Mesh(new IcosahedronGeometry(radius, 1), material);
    body.position.copy(position);
    body.rotation.z = 0.35;
    put(planet.hex, body);
    spinning.push(body);

    if (big) {
      const ring = new Mesh(new TorusGeometry(radius * 1.55, 0.05, 3, 18), flatMaterial('#e3c9a3'));
      ring.position.copy(position);
      ring.rotation.x = Math.PI / 2.4;
      put(planet.hex, ring);
    }

    if (planet.homeOf) {
      const faction = playerFactions.get(planet.homeOf);
      if (!faction) continue;
      const marker = new Mesh(
        new RingGeometry(0.82, 0.98, 6),
        new MeshBasicMaterial({ color: seatColorHex(planet.homeOf) }),
      );
      marker.rotation.x = -Math.PI / 2;
      marker.rotation.z = Math.PI / 6;
      marker.position.copy(hexToWorld(planet.hex, 0.02));
      put(planet.hex, marker);

      const element = document.createElement('div');
      element.className = 'home-label';
      element.style.setProperty('--faction', seatColorHex(planet.homeOf));
      const label = new CSS2DObject(element);
      label.position.copy(hexToWorld(planet.hex, radius * 2 + 0.5));
      put(planet.hex, label);
      labels.push({ playerId: planet.homeOf, faction, element });
    }
  }
  // Lagrange points (#127: they were not drawn at all): a violet diamond on the tile and a
  // small crystal over it, the colour of the exotics a station there yields.
  for (const l of system.lagrangePoints) {
    const diamond = new Mesh(
      new RingGeometry(0.34, 0.5, 4),
      new MeshBasicMaterial({ color: LAGRANGE_COLOR, transparent: true, opacity: 0.9 }),
    );
    const glow = new Mesh(
      new CircleGeometry(0.34, 4),
      new MeshBasicMaterial({ color: LAGRANGE_COLOR, transparent: true, opacity: 0.18 }),
    );
    for (const flat of [diamond, glow]) {
      flat.rotation.x = -Math.PI / 2;
      flat.position.copy(hexToWorld(l.hex, 0.03));
      flat.layers.set(LAYER_NO_OUTLINE);
      put(l.hex, flat);
    }
    const crystal = new Mesh(
      new OctahedronGeometry(0.18, 0),
      flatMaterial(LAGRANGE_COLOR, { emissive: LAGRANGE_COLOR, emissiveIntensity: 0.5 }),
    );
    crystal.position.copy(hexToWorld(l.hex, 0.32));
    put(l.hex, crystal);
    spinning.push(crystal);
  }
  return { group, spinning, labels, byHex };
}

/**
 * Dust clouds as a few soft puffs per hex. All puffs of one colour share an
 * InstancedMesh: three draw calls instead of a few hundred (#55). The puffs
 * are drawn from the same seeded sequence as before, so the clouds look the same.
 */
function buildClouds(system: StarSystem): {
  group: Group;
  fog(explored: ReadonlySet<string> | null): void;
  disperse(cloudIds: ReadonlySet<string>): void;
  update(seconds: number): void;
} {
  const group = new Group();
  const byColor = new Map<string, Puff[]>();
  const puff = new Object3D();
  for (const cloud of system.clouds) {
    const rng = Rng.fromSeed(`cloud:${system.seed}:${cloud.id}`);
    const color = rng.pick(SCENE_COLORS.cloud);
    const matrices = byColor.get(color) ?? [];
    byColor.set(color, matrices);
    for (const h of cloud.hexes) {
      // Above the grid (#99): the dust floats over the tiles, never under them.
      const center = hexToWorld(h, 0.5);
      const puffs = 4 + cloud.level;
      for (let i = 0; i < puffs; i++) {
        const radius = rng.range(0.45, 0.75);
        puff.position.set(
          center.x + rng.range(-0.5, 0.5),
          center.y + rng.range(-0.05, 0.25),
          center.z + rng.range(-0.5, 0.5),
        );
        puff.scale.set(radius, radius * 0.45, radius);
        puff.rotation.set(0, rng.range(0, 3), 0);
        puff.updateMatrix();
        matrices.push({ matrix: puff.matrix.clone(), key: hexKey(h), cloud: cloud.id });
      }
    }
  }
  const geometry = new IcosahedronGeometry(1, 1);
  const meshes: { mesh: InstancedMesh; puffs: Puff[] }[] = [];
  for (const [color, matrices] of byColor) {
    // Diffuse dust has no hard silhouette, so clouds skip the outline pass.
    const material = new MeshLambertMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.35,
      transparent: true,
      opacity: 0.32,
      depthWrite: false,
      flatShading: true,
    });
    const mesh = new InstancedMesh(geometry, material, matrices.length);
    // Puffs over unexplored hexes start shrunk to the centre, so bounds taken then miss
    // the clouds found later: they showed only at some camera angles (#150).
    mesh.frustumCulled = false;
    matrices.forEach((m, i) => mesh.setMatrixAt(i, m.matrix));
    mesh.instanceMatrix.needsUpdate = true;
    mesh.layers.set(LAYER_NO_OUTLINE);
    group.add(mesh);
    meshes.push({ mesh, puffs: matrices });
  }
  // A puff over an unexplored hex shrinks to nothing (#97); a cloud scanned
  // to the end shrinks away over a second and a half (#103).
  const none = new Matrix4().makeScale(0, 0, 0);
  const scaled = new Matrix4();
  const shrink = new Matrix4();
  let explored: ReadonlySet<string> | null = null;
  /** Dispersed clouds and when they began to fade; NaN until the next frame stamps it. */
  const gone = new Map<string, number>();
  let now = 0;
  let fading = false;
  const apply = () => {
    fading = false;
    for (const { mesh, puffs } of meshes) {
      puffs.forEach((p, i) => {
        const start = gone.get(p.cloud);
        if (explored && !explored.has(p.key)) mesh.setMatrixAt(i, none);
        else if (start === undefined) mesh.setMatrixAt(i, p.matrix);
        else {
          const k = Number.isNaN(start) ? 1 : Math.max(0, 1 - (now - start) / CLOUD_FADE_SECONDS);
          if (k > 0) fading = true;
          mesh.setMatrixAt(
            i,
            k > 0 ? scaled.copy(p.matrix).multiply(shrink.makeScale(k, k, k)) : none,
          );
        }
      });
      mesh.instanceMatrix.needsUpdate = true;
    }
  };
  return {
    group,
    fog(next) {
      explored = next;
      apply();
    },
    disperse(cloudIds) {
      let added = false;
      for (const id of cloudIds) {
        if (gone.has(id)) continue;
        gone.set(id, Number.NaN);
        added = true;
      }
      if (added) {
        fading = true;
        apply();
      }
    },
    update(seconds) {
      now = seconds;
      if (!fading) return;
      for (const [id, start] of gone) if (Number.isNaN(start)) gone.set(id, seconds);
      apply();
    },
  };
}

/** One puff of dust: where it floats, its hex and its cloud. */
interface Puff {
  readonly matrix: Matrix4;
  readonly key: string;
  readonly cloud: string;
}

const CLOUD_FADE_SECONDS = 1.5;

export function buildSystemView(
  system: StarSystem,
  playerFactions: ReadonlyMap<string, FactionId>,
): SystemView {
  const root = new Group();
  const tiles = buildTiles(system);
  root.add(tiles.group);
  const star = buildStar(system);
  root.add(star.group);
  const planets = buildPlanets(system, playerFactions);
  root.add(planets.group);
  const clouds = buildClouds(system);
  root.add(clouds.group);
  const sky = buildSky(system.seed, system.mapRadius);
  root.add(sky.root);

  return {
    root,
    labels: planets.labels,
    tintSector: tiles.tint,
    disperseClouds: clouds.disperse,
    setFog(visible, explored = null) {
      tiles.fog(visible, explored);
      clouds.fog(explored);
      for (const [key, objects] of planets.byHex) {
        const shown = !explored || explored.has(key);
        for (const o of objects) o.visible = shown;
      }
    },
    update(seconds) {
      sky.update(seconds);
      clouds.update(seconds);
      planets.spinning.forEach((o, i) => {
        o.rotation.y = seconds * (0.15 + (i % 5) * 0.04);
      });
    },
    dispose() {
      root.traverse((object) => {
        if (object instanceof Sprite) {
          // Sprites share one global geometry in three.js: dispose only the material.
          object.material.map?.dispose();
          object.material.dispose();
        } else if (object instanceof Mesh || object instanceof Points) {
          object.geometry.dispose();
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          for (const material of materials) material.dispose();
        }
        if (object instanceof CSS2DObject) object.element.remove();
      });
    },
  };
}

export function starLightColor(system: StarSystem): Color {
  const [r, g, b] = blackbodyRgb(STAR_CLASSES[system.starClass].temperatureK);
  return new Color(r, g, b);
}
